import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiChevronRight, FiPlay, FiCheckCircle, FiClock, FiAlertCircle, FiSettings, FiMail, FiGlobe, FiBarChart, FiEdit, FiZap } = FiIcons;

const CoreModules = () => {
  const [expandedModule, setExpandedModule] = useState(0);

  const modules = [
    {
      id: 'website-management',
      title: 'Website Management Module',
      icon: FiGlobe,
      purpose: 'Streamline website updates, SEO tasks, and content publishing with automated workflows and quality checks.',
      workflow: [
        {
          step: 'Content Request Received',
          description: 'New content update request enters the system via form or email',
          inputs: ['Content brief', 'Target pages', 'Deadline', 'Priority level'],
          outputs: ['Task assignment', 'Initial review', 'Resource allocation']
        },
        {
          step: 'Content Preparation',
          description: 'Coordinator prepares content following SEO and brand guidelines',
          inputs: ['Brand guidelines', 'SEO checklist', 'Content templates'],
          outputs: ['Draft content', 'SEO optimization', 'Meta data']
        },
        {
          step: 'Quality Review',
          description: 'Automated and manual quality checks ensure standards compliance',
          inputs: ['Content draft', 'Quality checklist', 'Brand standards'],
          outputs: ['Quality score', 'Revision notes', 'Approval status']
        },
        {
          step: 'Publishing & Monitoring',
          description: 'Content goes live with automated monitoring and performance tracking',
          inputs: ['Approved content', 'Publishing schedule', 'Analytics setup'],
          outputs: ['Live content', 'Performance metrics', 'Success report']
        }
      ],
      qualityStandards: [
        'SEO optimization score > 85%',
        'Brand compliance verification',
        'Mobile responsiveness check',
        'Page load speed < 3 seconds',
        'Accessibility standards (WCAG 2.1)'
      ],
      automations: [
        'Auto-generate meta descriptions from content',
        'Schedule publishing for optimal times',
        'Monitor page performance and alert on issues',
        'Update internal linking automatically',
        'Generate SEO performance reports'
      ]
    },
    {
      id: 'email-marketing',
      title: 'Email Marketing Module',
      icon: FiMail,
      purpose: 'Automate email campaign creation, testing, and optimization while maintaining consistent brand messaging.',
      workflow: [
        {
          step: 'Campaign Planning',
          description: 'Define campaign objectives, audience, and success metrics',
          inputs: ['Campaign brief', 'Target segments', 'Goals & KPIs'],
          outputs: ['Campaign strategy', 'Timeline', 'Resource requirements']
        },
        {
          step: 'Content Creation',
          description: 'Develop email content using templates and brand guidelines',
          inputs: ['Email templates', 'Brand assets', 'Content calendar'],
          outputs: ['Email design', 'Copy content', 'CTA optimization']
        },
        {
          step: 'Testing & Optimization',
          description: 'A/B testing and quality assurance before campaign launch',
          inputs: ['Test segments', 'Variation parameters', 'Success criteria'],
          outputs: ['Test results', 'Winning variation', 'Performance predictions']
        },
        {
          step: 'Launch & Monitor',
          description: 'Campaign deployment with real-time monitoring and optimization',
          inputs: ['Final content', 'Send schedule', 'Monitoring setup'],
          outputs: ['Campaign metrics', 'Performance alerts', 'Optimization recommendations']
        }
      ],
      qualityStandards: [
        'Deliverability score > 95%',
        'Mobile optimization verified',
        'Spam score < 3.0',
        'CTA click-through rate targets',
        'Brand consistency validation'
      ],
      automations: [
        'Segment audiences based on behavior',
        'Personalize content dynamically',
        'Schedule optimal send times',
        'Generate performance reports',
        'Trigger follow-up sequences'
      ]
    },
    {
      id: 'analytics-reporting',
      title: 'Analytics & Reporting Module',
      icon: FiBarChart,
      purpose: 'Automate data collection, analysis, and reporting to provide actionable insights for marketing optimization.',
      workflow: [
        {
          step: 'Data Collection',
          description: 'Gather data from all marketing channels and touchpoints',
          inputs: ['Analytics platforms', 'Marketing tools', 'CRM systems'],
          outputs: ['Unified dataset', 'Data quality score', 'Missing data alerts']
        },
        {
          step: 'Analysis & Processing',
          description: 'Process data to identify trends, patterns, and insights',
          inputs: ['Raw data', 'Analysis templates', 'Benchmark data'],
          outputs: ['Key insights', 'Trend analysis', 'Anomaly detection']
        },
        {
          step: 'Report Generation',
          description: 'Create automated reports with visualizations and recommendations',
          inputs: ['Processed data', 'Report templates', 'Stakeholder preferences'],
          outputs: ['Executive reports', 'Detailed analytics', 'Action items']
        },
        {
          step: 'Distribution & Follow-up',
          description: 'Distribute reports and track action item completion',
          inputs: ['Final reports', 'Distribution lists', 'Follow-up schedule'],
          outputs: ['Report delivery', 'Stakeholder feedback', 'Action tracking']
        }
      ],
      qualityStandards: [
        'Data accuracy > 98%',
        'Report delivery within 24 hours',
        'Actionable insights in every report',
        'Consistent formatting and branding',
        'Stakeholder satisfaction > 4.5/5'
      ],
      automations: [
        'Daily data synchronization',
        'Automated report generation',
        'Alert system for significant changes',
        'Benchmark comparison tracking',
        'Action item workflow triggers'
      ]
    },
    {
      id: 'content-coordination',
      title: 'Content Coordination Module',
      icon: FiEdit,
      purpose: 'Orchestrate content creation, review, and publishing across all marketing channels with streamlined approval workflows.',
      workflow: [
        {
          step: 'Content Planning',
          description: 'Strategic content planning aligned with marketing objectives',
          inputs: ['Content strategy', 'Editorial calendar', 'Resource availability'],
          outputs: ['Content roadmap', 'Assignment schedule', 'Resource allocation']
        },
        {
          step: 'Creation & Review',
          description: 'Coordinate content creation with quality review processes',
          inputs: ['Content briefs', 'Brand guidelines', 'Review criteria'],
          outputs: ['Draft content', 'Review feedback', 'Revision requests']
        },
        {
          step: 'Approval Workflow',
          description: 'Multi-stage approval process ensuring quality and compliance',
          inputs: ['Final drafts', 'Approval matrix', 'Compliance checklist'],
          outputs: ['Approval status', 'Final content', 'Publishing clearance']
        },
        {
          step: 'Publishing & Tracking',
          description: 'Coordinate publishing across channels with performance tracking',
          inputs: ['Approved content', 'Publishing schedule', 'Channel specifications'],
          outputs: ['Published content', 'Performance metrics', 'Engagement tracking']
        }
      ],
      qualityStandards: [
        'Brand compliance score > 95%',
        'Content approval within 48 hours',
        'Publishing accuracy 100%',
        'Engagement rate targets met',
        'Content quality score > 4.0/5'
      ],
      automations: [
        'Content calendar management',
        'Automated approval routing',
        'Cross-channel publishing',
        'Performance tracking setup',
        'Content asset organization'
      ]
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Core Modules</h1>
        <p className="text-lg text-gray-600 max-w-3xl">
          Detailed breakdown of each system module with step-by-step workflows, 
          quality standards, and automation opportunities for maximum efficiency.
        </p>
      </motion.div>

      {/* Module Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
      >
        {modules.map((module, index) => (
          <div
            key={module.id}
            onClick={() => setExpandedModule(index)}
            className={`glass-card rounded-xl p-4 cursor-pointer transition-all duration-300 hover-lift ${
              expandedModule === index ? 'ring-2 ring-emerald-500 bg-emerald-50' : ''
            }`}
          >
            <div className="flex items-center space-x-3 mb-2">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                expandedModule === index ? 'bg-emerald-500' : 'bg-gray-100'
              }`}>
                <SafeIcon icon={module.icon} className={`w-5 h-5 ${
                  expandedModule === index ? 'text-white' : 'text-gray-600'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-sm">{module.title.replace(' Module', '')}</h3>
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Detailed Module View */}
      <AnimatePresence mode="wait">
        <motion.div
          key={expandedModule}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="glass-card rounded-xl p-8"
        >
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-emerald-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={modules[expandedModule].icon} className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{modules[expandedModule].title}</h2>
              <p className="text-gray-600">{modules[expandedModule].purpose}</p>
            </div>
          </div>

          {/* Workflow Steps */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Step-by-Step Workflow</h3>
            <div className="space-y-6">
              {modules[expandedModule].workflow.map((step, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{step.step}</h4>
                      <p className="text-gray-600 mb-4">{step.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                            <SafeIcon icon={FiPlay} className="w-4 h-4 text-emerald-600 mr-2" />
                            Inputs Required
                          </h5>
                          <ul className="space-y-1">
                            {step.inputs.map((input, inputIndex) => (
                              <li key={inputIndex} className="text-sm text-gray-600 flex items-center">
                                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2"></div>
                                {input}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-gray-900 mb-2 flex items-center">
                            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-blue-600 mr-2" />
                            Outputs Generated
                          </h5>
                          <ul className="space-y-1">
                            {step.outputs.map((output, outputIndex) => (
                              <li key={outputIndex} className="text-sm text-gray-600 flex items-center">
                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                                {output}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quality Standards & Automations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-600 mr-2" />
                Quality Standards
              </h3>
              <div className="bg-emerald-50 rounded-xl p-6">
                <ul className="space-y-3">
                  {modules[expandedModule].qualityStandards.map((standard, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{standard}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiZap} className="w-5 h-5 text-purple-600 mr-2" />
                Automation Opportunities
              </h3>
              <div className="bg-purple-50 rounded-xl p-6">
                <ul className="space-y-3">
                  {modules[expandedModule].automations.map((automation, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <SafeIcon icon={FiZap} className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{automation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default CoreModules;